// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides functionality to fetch and process location reports.
 * It includes schemas for both functional status and ownership reports.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { sparseLocationWithParentSchema } from '@/sparse/api/types';
import { z } from 'zod';

/**
 * Schema for location functional status reports.
 * Tracks the count of functional and non-functional assets at a location.
 * Automatically calculates the total count of assets.
 */
const locationFunctionalStatusReport = z
  .object({
    location: sparseLocationWithParentSchema, // The location being reported on
    functionalCount: z.number(), // Count of functional assets
    needsReviewCount: z.number(), // Count of assets needing review
    nonFunctionalCount: z.number(), // Count of non-functional assets
  })
  .transform((report) => ({
    ...report,
    totalCount:
      report.functionalCount +
      report.needsReviewCount +
      report.nonFunctionalCount,
  }));

export type LocationFunctionalStatusReport = z.infer<
  typeof locationFunctionalStatusReport
>;

/**
 * Schema for location ownership reports.
 * Tracks the count of owned and sold assets at a location.
 * Automatically calculates the total count of assets.
 */
const locationOwnershipReport = z
  .object({
    location: sparseLocationWithParentSchema, // The location being reported on
    ownedCount: z.number(), // Count of owned assets
    soldCount: z.number(), // Count of sold assets
  })
  .transform((report) => ({
    ...report,
    totalCount: report.ownedCount + report.soldCount,
  }));

export type LocationOwnershipReport = z.infer<typeof locationOwnershipReport>;

/**
 * Schema for the complete location report response.
 * Includes both functional status and ownership reports for locations.
 */
const responseSchema = z
  .object({
    data: z.object({
      functional: z.array(locationFunctionalStatusReport),
      ownership: z.array(locationOwnershipReport),
    }),
  })
  .transform(({ data }) => data);

/**
 * Fetches comprehensive location reports from the API.
 * Includes both functional status and ownership information for all locations.
 *
 * @returns Promise resolving to an object containing arrays of functional status
 *          and ownership reports for all locations
 */
export async function getLocationReport() {
  const path = '/v1/location-reports';
  const response = await api.get(path);

  return parseResponse(responseSchema, response.data, {
    schemaName: 'LocationReport',
    endpoint: path,
  });
}
